import { useState, useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Key, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EditUserFormData } from '../../../../../schema/user';

interface PasswordResetSectionProps {
  control: Control<EditUserFormData>;
}

export default function PasswordResetSection({
  control,
}: PasswordResetSectionProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className={cn('space-y-4', isRTL && 'rtl')}>
      <div className={cn('space-y-2', isRTL && 'text-right')}>
        <div
          className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}
        >
          <Key className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">
            {t('users.edit.passwordReset.title')}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('users.edit.passwordReset.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={cn(isRTL && 'text-right')}>
                {t('users.edit.passwordReset.newPassword')}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder={t(
                      'users.edit.passwordReset.placeholders.newPassword'
                    )}
                    className={cn('pr-10', isRTL && 'text-right pl-10 pr-3')}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'absolute top-0 h-full px-3 py-2 hover:bg-transparent',
                      isRTL ? 'left-0' : 'right-0'
                    )}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={cn(isRTL && 'text-right')}>
                {t('users.edit.passwordReset.confirmNewPassword')}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t(
                      'users.edit.passwordReset.placeholders.confirmPassword'
                    )}
                    className={cn('pr-10', isRTL && 'text-right pl-10 pr-3')}
                    {...field}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'absolute top-0 h-full px-3 py-2 hover:bg-transparent',
                      isRTL ? 'left-0' : 'right-0'
                    )}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
